import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { reset as resetReduxForm } from 'redux-form';
import { TextKey } from '@mews-commander/enums';
import { LocalizedText } from '@mews/framework';
import { Button, Stack } from '@optimus-web/core';
import { FloatingPanel } from '@mews/b2b-ui';
import { EnterpriseScopeCounterDiscriminator, getRepositoryQueryKey } from '@mews-commander/data-access';
import { compactArray } from '@mews/fp';
import { InjectedFormProps, withHookForm } from '@mews-commander/form';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { useFormValueSelector } from '@mews-commander/legacy-form';
import { isDialogOpen } from '../../dialog/selectors';
import { closeDialog } from '../../dialog/reducer';
import { deleteCounter } from '../actions';
import { CounterForm } from './CounterForm';
import { CounterFormValues, defaultEmptyValues, useCountersForm } from './CounterForm/formMapping';
import { useConfirmDialogs } from './useConfirmDialogs';

type UnhookedCounterDialogProps = CounterDialogProps & InjectedFormProps;

const UnhookedCounterDialog = ({ name, submit, isEditForm, defaultValues }: UnhookedCounterDialogProps) => {
    const active = useCommanderSelector((state) => isDialogOpen(state, { name }));
    const [loading, setLoading] = useState(false);
    const dispatch = useCommanderThunkDispatch();
    const {
        reset,
        formState: { dirtyFields, isSubmitSuccessful, isSubmitting, isLoading, isSubmitted },
        watch,
        trigger,
    } = useFormContext<CounterFormValues>();

    const counterId = useFormValueSelector(name, 'counterId');
    const counterType: EnterpriseScopeCounterDiscriminator = useFormValueSelector(name, 'counterType');
    const titleTextKey = useFormValueSelector(name, 'titleTextKey');
    const isDefault = watch('isDefault');
    const queryClient = useQueryClient();

    const refetchCounter = useCallback(
        async () => queryClient.invalidateQueries({ queryKey: getRepositoryQueryKey('counters', 'getAll') }),
        [queryClient]
    );

    const close = useCallback(() => {
        dispatch(closeDialog(name));
        dispatch(resetReduxForm(name));

        reset(defaultValues, { keepDirty: false });
    }, [dispatch, name, reset, defaultValues]);

    const onCancel = () => {
        if (Object.entries(dirtyFields).length) {
            setIsCloseConfirmationOpen(true);
        } else {
            close();
        }
    };

    const showSetDefaultConfirmation =
        (isDefault && !isEditForm) || (isDefault && isEditForm && defaultValues?.isDefault === false);

    const onSubmitAttempt = async () => {
        const isValid = await trigger();
        if (!isValid) {
            return;
        }

        if (showSetDefaultConfirmation) {
            setIsSetDefaultConfirmationOpen(true);
        } else {
            await submit();
        }
    };

    const handleDelete = async () => {
        try {
            setLoading(true);
            await dispatch(deleteCounter(counterId));
            await refetchCounter();
        } finally {
            setLoading(false);
        }
    };

    const { Dialogs, setIsSetDefaultConfirmationOpen, setIsDeleteConfirmationOpen, setIsCloseConfirmationOpen } =
        useConfirmDialogs({
            closeAction: close,
            submit,
            handleDelete,
            onSubmitAttempt,
        });

    useEffect(() => {
        if (isSubmitted && isSubmitSuccessful) {
            const handleClose = async () => {
                setLoading(true);
                try {
                    await refetchCounter();
                    close();
                } finally {
                    setLoading(false);
                }
            };
            void handleClose();
        }

        if (isSubmitted && !isSubmitSuccessful) {
            setIsSetDefaultConfirmationOpen(false);
        }
    }, [isSubmitted, isSubmitSuccessful, refetchCounter, close, setIsSetDefaultConfirmationOpen]);

    if (!active) {
        return null;
    }

    return (
        <>
            <FloatingPanel
                active={active}
                loading={loading || isSubmitting || isLoading}
                onClose={close}
                title={<LocalizedText textKey={titleTextKey} />}
                actions={compactArray([
                    isEditForm
                        ? {
                              variant: 'secondary',
                              children: <LocalizedText textKey={TextKey.Delete} />,
                              size: 'medium',
                              onClick: () => setIsDeleteConfirmationOpen(true),
                              disabled: isDefault,
                              type: 'button',
                          }
                        : {
                              variant: 'secondary',
                              children: <LocalizedText textKey={TextKey.ClearForm} />,
                              size: 'medium',
                              onClick: () => reset(defaultEmptyValues, { keepDirty: false }),
                              type: 'button',
                          },
                    {
                        variant: 'primary',
                        children: <LocalizedText textKey={TextKey.Save} />,
                        onClick: onSubmitAttempt,
                        size: 'medium',
                        type: 'submit',
                    },
                ])}
                footerDetail={
                    <Stack>
                        <Button variant="tertiary" onClick={onCancel} size="medium" type="submit">
                            <LocalizedText textKey={TextKey.Cancel} />
                        </Button>
                    </Stack>
                }
            >
                <CounterForm counterType={counterType} isEditForm={isEditForm} />
            </FloatingPanel>
            {Dialogs}
        </>
    );
};

export const HookedCounterDialog = withHookForm<CounterFormValues, UnhookedCounterDialogProps>()(UnhookedCounterDialog);

interface CounterDialogProps {
    name: string;
    onSubmit: (values: CounterFormValues) => void;
    defaultValues?: CounterFormValues;
    isEditForm?: boolean;
}

export const CounterDialog = ({ defaultValues, ...props }: CounterDialogProps) => {
    const formProps = useCountersForm({ defaultValues, name: props.name, isEditForm: props.isEditForm });
    return <HookedCounterDialog useFormProps={formProps} defaultValues={defaultValues} {...props} />;
};
